/* ============================================================
   Freight emissions — GLEC Framework v3 / ISO 14083:2023

   Shippers are starting to ask East African carriers for a gCO₂e per
   tonne-km figure, and almost none can produce one. This module exists
   so an operator can answer a tender question with a defensible number
   and a stated data-quality level, rather than a guess or a silence.

   THE DATA QUALITY HIERARCHY IS THE POINT.
   ISO 14083 does not just want a number, it wants to know how you got
   it. Three levels, best first:

     primary   — measured fuel actually consumed on the leg
     modelled  — fuel derived from your own consumption rate
     default   — GLEC default intensity for the mode

   A default-derived figure is legitimate and reportable, but a shipper
   will discount it against a competitor's primary data. The tool says
   so plainly rather than presenting all three as equivalent.

   All figures are WELL-TO-WHEEL (WTW): the fuel burned in the vehicle
   plus the emissions of producing and delivering that fuel. ISO 14083
   requires WTW. Reporting tank-to-wheel only understates a diesel
   operation by roughly 18–20%, so the tool never does it silently.
   ============================================================ */

/** Well-to-wheel emission factors per litre of fuel, kgCO₂e. */
export const FUEL_FACTORS = {
  diesel: { label: 'Diesel', wtw: 3.17, ttw: 2.68 },
  petrol: { label: 'Petrol', wtw: 2.99, ttw: 2.35 }
};

/**
 * GLEC default WTW intensities, gCO₂e per tonne-kilometre.
 *
 * These are framework defaults, not Kenyan measurements. They are the
 * correct fallback when primary data is unavailable — which is what
 * GLEC intends them for — but an operator with fuel records should
 * always use those instead.
 */
export const MODE_DEFAULTS = {
  'road-artic': {
    label: 'Road — articulated, over 33 t',
    intensity: 62,
    typicalPayload: 26,
    note: 'The standard Northern Corridor line-haul configuration.'
  },
  'road-rigid': {
    label: 'Road — rigid, 7.5–20 t',
    intensity: 137,
    typicalPayload: 12,
    note: 'Distribution and shorter regional runs. Materially worse per tonne-km.'
  },
  'road-light': {
    label: 'Road — light commercial, under 3.5 t',
    intensity: 620,
    typicalPayload: 1.2,
    note: 'Last mile. An order of magnitude worse per tonne-km than line haul.'
  },
  'rail-diesel': {
    label: 'Rail — diesel hauled',
    intensity: 30,
    typicalPayload: 20,
    note: 'Kenya’s SGR freight is diesel hauled, so the diesel factor applies, not the electric one.'
  },
  'sea-container': {
    label: 'Sea — container vessel',
    intensity: 12,
    typicalPayload: 20,
    note: 'Deep-sea leg. Dominates distance but rarely dominates emissions.'
  },
  air: {
    label: 'Air freight',
    intensity: 810,
    typicalPayload: 5,
    note: 'Roughly thirteen times road per tonne-km. Use only where the cargo demands it.'
  }
};

export function modeDefault(id) {
  return Object.prototype.hasOwnProperty.call(MODE_DEFAULTS, id) ? MODE_DEFAULTS[id] : null;
}

/**
 * Emissions for a single transport chain element (a "leg" in ISO 14083).
 *
 * @param {object} leg
 *   mode        key into MODE_DEFAULTS
 *   distance    km
 *   payload     tonnes actually carried
 *   fuelLitres  measured fuel for the leg, if known → primary data
 *   fuelRate    litres per 100 km, if known → modelled data
 *   fuelType    key into FUEL_FACTORS
 */
export function assessLeg(leg) {
  const { mode, distance, payload, fuelLitres, fuelRate, fuelType = 'diesel' } = leg;

  const modeInfo = modeDefault(mode);
  const factor = FUEL_FACTORS[fuelType] || FUEL_FACTORS.diesel;
  const tonneKm = payload * distance;

  let kgCO2e;
  let quality;
  let basis;

  if (Number.isFinite(fuelLitres) && fuelLitres > 0) {
    kgCO2e = fuelLitres * factor.wtw;
    quality = 'primary';
    basis = `${fuelLitres.toFixed(0)} L measured × ${factor.wtw} kgCO₂e/L (well-to-wheel)`;
  } else if (Number.isFinite(fuelRate) && fuelRate > 0) {
    const litres = (fuelRate / 100) * distance;
    kgCO2e = litres * factor.wtw;
    quality = 'modelled';
    basis = `${fuelRate} L/100 km × ${distance} km = ${litres.toFixed(0)} L × ${factor.wtw} kgCO₂e/L`;
  } else if (modeInfo) {
    kgCO2e = (modeInfo.intensity * tonneKm) / 1000;
    quality = 'default';
    basis = `GLEC default ${modeInfo.intensity} gCO₂e/t-km × ${tonneKm.toFixed(0)} t-km`;
  } else {
    return null;
  }

  return {
    ...leg,
    modeLabel: modeInfo ? modeInfo.label : mode,
    tonneKm,
    kgCO2e,
    intensity: tonneKm > 0 ? (kgCO2e * 1000) / tonneKm : 0,
    quality,
    basis
  };
}

export const QUALITY_META = {
  primary: {
    label: 'Primary',
    rank: 3,
    tone: 'var(--ca-positive)',
    note: 'Measured fuel. The strongest evidence you can present.'
  },
  modelled: {
    label: 'Modelled',
    rank: 2,
    tone: 'var(--ca-warning)',
    note: 'Derived from your own consumption rate. Credible, and improved by fuel records.'
  },
  default: {
    label: 'Default',
    rank: 1,
    tone: 'var(--ca-text-quaternary)',
    note: 'GLEC framework default. Reportable, but a shipper will discount it against primary data.'
  }
};

/** Roll several legs into a chain-level result. */
export function assessChain(legs) {
  const assessed = legs.map(assessLeg).filter(Boolean);
  if (assessed.length === 0) return null;

  const totalKg = assessed.reduce((sum, l) => sum + l.kgCO2e, 0);
  const totalTonneKm = assessed.reduce((sum, l) => sum + l.tonneKm, 0);

  // The chain is only as strong as its weakest leg — ISO 14083 reports
  // data quality at the level actually achieved, not the best achieved.
  const weakest = assessed.reduce(
    (worst, l) => (QUALITY_META[l.quality].rank < QUALITY_META[worst].rank ? l.quality : worst),
    'primary'
  );

  // How much of the answer is actually evidenced, three ways.
  //
  // Counting legs flatters the chain badly. A measured 485 km road leg
  // beside a GLEC-default 8,000 km sea leg is "50% of legs measured" —
  // but 7% of the tonne-kilometres and 23% of the emissions. A shipper
  // running their own diligence weights by the work done, not by how the
  // journey happens to be chopped into rows, so report what they will
  // compute rather than the flattering count.
  const primary = assessed.filter((l) => l.quality === 'primary');
  const share = (part, whole) => (whole > 0 ? part / whole : 0);

  const evidence = {
    legs: share(primary.length, assessed.length),
    tonneKm: share(
      primary.reduce((sum, l) => sum + l.tonneKm, 0),
      totalTonneKm
    ),
    emissions: share(
      primary.reduce((sum, l) => sum + l.kgCO2e, 0),
      totalKg
    )
  };

  return {
    legs: assessed,
    totalKg,
    totalTonneKm,
    intensity: totalTonneKm > 0 ? (totalKg * 1000) / totalTonneKm : 0,
    quality: weakest,
    evidence
  };
}

/**
 * What the same journey would emit on another mode, at GLEC defaults.
 * Used to size a modal-shift opportunity, not to claim a saving already made.
 */
export function compareMode(totalTonneKm, modeId) {
  const info = modeDefault(modeId);
  if (!info || totalTonneKm <= 0) return null;
  return { mode: info, kgCO2e: (info.intensity * totalTonneKm) / 1000 };
}

/** Rough equivalences, for a board pack rather than a compliance return. */
export function equivalences(kgCO2e) {
  return {
    // A mature tree sequesters roughly 21 kg CO₂ a year.
    treeYears: kgCO2e / 21,
    // Average passenger car, roughly 170 gCO₂e per km.
    carKm: (kgCO2e * 1000) / 170
  };
}
